import { Link } from 'react-router'
import type { ReactNode } from 'react'
import { Bell, KeyRound, LogOut, Menu, PanelLeft, UserRound } from 'lucide-react'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { roleLabel } from '@/domain/words'
import { useExpert } from '@/hooks/useExpert'
import { cn } from '@/lib/utils'
import type { User } from '@/api/types'

// 상단 바. 우측 묶음이 전문가 토글·알림·계정을 갖는다 — 독립된 나가기 버튼은 없다.
//
// 알림 배지는 실제 승인 대기 신청 수다. 검색 칸의 자리는 여기고 내용(`views/TopSearch.tsx`)은 부르는 쪽이 넣는다 —
// 검색은 관리자 껍데기가 읽은 목록을 보므로 셸의 틀이 그 목록을 알 일이 없다.

export function Topbar({
  user,
  search,
  pending,
  onToggleSidebar,
  onOpenNav,
  onLogout,
}: {
  user: User
  search?: ReactNode
  pending: number
  onToggleSidebar: () => void
  onOpenNav?: () => void
  onLogout: () => void
}) {
  const expert = useExpert()
  return (
    <header className="flex h-14 flex-none items-center gap-3 border-b bg-surface px-4">
      {/* 좁은 화면에서는 레일과 사이드바가 서랍에 있어 손잡이가 그것을 열고, 접기는 넓은 화면의 것이다. */}
      {onOpenNav && (
        <Button
          variant="ghost"
          size="icon"
          onClick={onOpenNav}
          aria-label="메뉴 열기"
          className="text-muted-foreground hover:text-foreground lg:hidden"
        >
          <Menu className="size-4" />
        </Button>
      )}

      <Button
        variant="ghost"
        size="icon"
        onClick={onToggleSidebar}
        aria-label="사이드바 접기"
        className="text-muted-foreground hover:text-foreground max-lg:hidden"
      >
        <PanelLeft className="size-4" />
      </Button>

      <div className="relative w-full max-w-80 max-sm:hidden">{search}</div>

      <div className="flex-1" />

      {/* 화면 모드 — 일반은 행정가의 화면, 고급은 같은 자리에 상세 값이 더 선다. 지금 모드가 늘 보인다. */}
      <div role="group" aria-label="화면 모드" className="flex h-8 items-center rounded-full border p-0.5 text-[12px]">
        {([
          ['일반', false],
          ['고급', true],
        ] as const).map(([label, on]) => (
          <button
            key={label}
            type="button"
            aria-pressed={expert.on === on}
            onClick={() => (expert.on === on ? undefined : expert.toggle())}
            className={cn(
              'h-full rounded-full px-3 transition-colors',
              expert.on === on ? 'bg-navy text-white' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {label}
          </button>
        ))}
      </div>

      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            asChild
            variant="ghost"
            size="icon"
            aria-label={pending > 0 ? `검토 대기 ${pending}건` : '검토 대기 없음'}
            className="relative text-muted-foreground hover:text-foreground"
          >
            <Link to="/">
              <Bell className="size-4" />
              {pending > 0 && (
                <span className="absolute top-1 right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-warn px-1 text-[10px] font-semibold text-black">
                  {pending}
                </span>
              )}
            </Link>
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          {pending > 0 ? `검토 대기 ${pending}건` : '검토 대기 없음'}
        </TooltipContent>
      </Tooltip>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-9 gap-2 px-2 hover:bg-surface-2">
            <Avatar className="size-7">
              <AvatarFallback className="bg-brand text-[11px] font-semibold text-white">
                {user.username.slice(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <span className="text-[13px] max-sm:hidden">{user.username}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuLabel className="font-normal">
            <div className="text-[13px]">{user.username}</div>
            <div className="text-[11px] text-muted-foreground">
              {roleLabel[user.role]}
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <Link to="/me">
              <UserRound className="size-3.5" />
              내 정보
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/me/keys">
              <KeyRound className="size-3.5" />
              SSH 키
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={onLogout}>
            <LogOut className="size-3.5" />
            로그아웃
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  )
}
